/* eslint-disable no-underscore-dangle */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import { ConfigService } from '../config/config.service';
import { ProductsConfig } from '../../class/config.type';
import { WorkflowInitializeService } from '../workflow-initialize/workflow-initialize.service';

/**
 * Initializer service for getting external workflow config from DB or any other sources
 */
@Injectable({
  providedIn: 'root'
})
export class WorkflowExternalConfigService {
  /**
   * A subject on external config merge event
   */
  private readonly _externalConfigMerged: Subject<boolean> = new Subject<boolean>();

  /**
   * If external config is loaded in script tag
   */
  // eslint-disable-next-line @typescript-eslint/dot-notation
  private readonly _externalConfig: ProductsConfig = window['externalConfig'];

  /**
   * Reference to externalConfigMerged event as an observable
   */
  public get externalConfigMerged$(): Observable<boolean> {
    return this._externalConfigMerged.asObservable();
  }

  constructor(
    private readonly configService: ConfigService,
    private readonly workflowInitializeService: WorkflowInitializeService,
    private readonly httpClient: HttpClient
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public loadExternalConfig(products: string[], configApi?: string, customConfig?: any): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (this._externalConfig) {
        this.addExternalConfig(this._externalConfig);
        resolve(true);
      } else {
        this.getConfigData(products, resolve, reject, configApi, customConfig);
      }
    });
  }

  /** Get Config from JSON end point */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  private getConfigData(products: string[], resolve, reject, configApi?: string, customConfig?: any): void {
    const configApiUrl = configApi;
    /**
     * This call gets the workflow config from the API
     * Example: https://dev1-api.cudirect.com/cpapi/api/workflow/config?products=oao,ola,mortgage
     * This operates of of Origin header (https://qa4-ootb.myori.com), but we may need to update
     * to use a tenantId/lender code in the request body/header or jwt if used from an back office app.
     */
    const headers = { 'x-api-version': '1.0' };

    this.httpClient
      .get(this.url(products, configApiUrl), { headers })
      .pipe(
        catchError((error) => this.handleError(error, products)),
        finalize(() => resolve())
      )
      .subscribe(
        (response) => {
          const config = (response || {}) as ProductsConfig;
          this.addExternalConfig(config);
        },
        (error) => {
          reject(error);
        }
      );
  }

  /** build config api url based on products */
  private readonly url = (products: string[], configApiUrl = '') => {
    const url = configApiUrl;
    const productsQuery = products && products.length ? `?products=${products.join(',')}` : '';
    return `${url}${productsQuery}`;
  };

  private handleError(error: HttpErrorResponse, products: string[]): Observable<never> {
    return throwError(() => ({
      error,
      message: `Error in getting workflow config from api. ${products && products.length ? `Products ${products.join(',')}` : ''}`
    }));
  }

  /**
   *  Init config and backend service when config is retrieved successfully
   *  @param config - product config
   */
  public readonly addExternalConfig = (config: ProductsConfig) => {
    this.configService.addExternalConfig(config);
    this.workflowInitializeService.initConfigs();
    this.workflowInitializeService.initBackend();
    this._externalConfigMerged.next(true);
  };
}
